<template>
  <div v-if="navContent.length" class="article-nav-content">
    <h3 class="title">Содержание статьи</h3>

    <div v-for="item of navContent" :key="item.uid" class="item" @click="scrollTo(item)">
      <div class="text-wrapper">
        <p class="text" :class="{ '-active': item.uid === activeItem }">
          {{ item.text }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  interface NavItem {
    uid: string
    text: string
    top: number
  }

  const ATTR_PREFIX = 'data-nav-id'
  const SCROLL_OFFSET = 120

  const navContent = ref<NavItem[]>([])
  const activeItem = ref('')

  let resizeObserver: ResizeObserver | null = null

  const collectHeadings = () => {
    const container = document.querySelector('.article-content')
    if (!container) return

    const headings = container.querySelectorAll('h1, h2, h3, h4')
    const items: NavItem[] = []

    headings.forEach((heading, index) => {
      const text = heading.textContent?.trim()
      if (!text) return

      const uid = `nav-heading-${index}`
      heading.setAttribute(ATTR_PREFIX, uid)
      items.push({
        uid,
        text,
        top: heading.getBoundingClientRect().top + window.scrollY,
      })
    })

    navContent.value = items
  }

  const recalculatePositions = () => {
    for (const item of navContent.value) {
      const el = document.querySelector(`[${ATTR_PREFIX}="${item.uid}"]`)
      if (el) {
        item.top = el.getBoundingClientRect().top + window.scrollY
      }
    }
  }

  const updateActiveItem = () => {
    const scrollY = window.scrollY
    let active = ''

    for (const item of navContent.value) {
      if (scrollY >= item.top - SCROLL_OFFSET) {
        active = item.uid
      }
    }

    activeItem.value = active
  }

  const scrollTo = (item: NavItem) => {
    const el = document.querySelector(`[${ATTR_PREFIX}="${item.uid}"]`)
    if (!el) return

    const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET + 20
    window.scrollTo({ top, behavior: 'smooth' })
  }

  const initObserver = () => {
    disableObserver()
    collectHeadings()
    if (!navContent.value.length) return

    updateActiveItem()
    window.addEventListener('scroll', updateActiveItem, { passive: true })

    const container = document.querySelector('.article-content')
    if (container) {
      resizeObserver = new ResizeObserver(recalculatePositions)
      resizeObserver.observe(container)
    }
  }

  const disableObserver = () => {
    window.removeEventListener('scroll', updateActiveItem)
    resizeObserver?.disconnect()
    resizeObserver = null
  }

  onMounted(() => {
    nextTick(() => initObserver())
  })

  onBeforeUnmount(() => {
    disableObserver()
  })

  defineExpose({ initObserver, disableObserver })
</script>

<style lang="scss" scoped>
  .article-nav-content {
    @extend %flex-column;

    padding: 24px;
    border-radius: 24px;
    background-color: $color-default-white;
    max-height: 340px;
    overflow-y: auto;

    @include vp-767 {
      padding: 20px;
    }

    > .title {
      font-size: 20px;
      font-weight: 500;
      line-height: 28px;
      margin-bottom: 14px;
    }

    > .item {
      padding: 6px 0 6px 10px;
      cursor: pointer;
      position: relative;

      &::after {
        content: '';
        width: 2px;
        height: 100%;
        border-radius: 12px;
        background-color: $color-kiwi-20;
        position: absolute;
        left: 0;
        top: 0;
      }

      > .text-wrapper > .text {
        font-size: 16px;
        line-height: 22px;
        transition: color $trans-default;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;

        &:hover:not(.-active) {
          color: $color-kiwi-50;
        }

        &.-active {
          font-weight: 600;
          color: $color-kiwi-50;

          &::after {
            content: '';
            position: absolute;
            left: -3px;
            top: calc(50% - 4px);
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: $color-kiwi-50;
            z-index: 2;
          }
        }
      }
    }
  }
</style>
